/**
 * product-lightbox.js
 * Premium fullscreen product image gallery — Shopify OS 2.0 native.
 * Vanilla JS, zero dependencies. Reusable across themes.
 *
 * Usage:
 *   <div data-lightbox> ... markup from snippets/product-lightbox.liquid ... </div>
 *   ProductLightbox.init(document.querySelector('[data-product-lightbox-root]'));
 *
 * Public API (per instance, stored on the root element as `el.lightboxInstance`):
 *   .open(index)
 *   .close()
 *   .goTo(index)
 *   .goToMediaId(mediaId)
 *   .destroy()
 */
(function (window, document) {
  'use strict';

  var ZOOM_MIN = 1;
  var ZOOM_MAX = 4;
  var ZOOM_STEP_WHEEL = 0.0018; // multiplied by wheel deltaY
  var DOUBLE_TAP_ZOOM = 2;
  var SWIPE_THRESHOLD = 40; // px
  var SWIPE_VELOCITY_THRESHOLD = 0.35; // px/ms

  /* ---------------------------------------------------------------------
   * Small utilities
   * ------------------------------------------------------------------- */
  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function distanceBetweenTouches(touches) {
    var dx = touches[0].clientX - touches[1].clientX;
    var dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function midpoint(touches) {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2
    };
  }

  /* ---------------------------------------------------------------------
   * ProductLightbox
   * ------------------------------------------------------------------- */
  function ProductLightbox(root) {
    if (!root || root.lightboxInstance) return root ? root.lightboxInstance : null;

    this.root = root;
    this.triggersRoot = document.querySelector('[data-lightbox-triggers][data-lightbox-for="' + root.dataset.lightboxId + '"]') ||
      root.previousElementSibling; // falls back to sibling trigger grid emitted by the snippet

    this.backdrop = qs(root, '[data-lightbox-close]');
    this.slidesWrap = qs(root, '[data-lightbox-slides]');
    this.slides = qsa(root, '[data-lightbox-slide]');
    this.thumbs = qsa(root, '[data-lightbox-thumb]');
    this.thumbsRail = qs(root, '[data-lightbox-thumbs]');
    this.counter = qs(root, '[data-lightbox-counter]');
    this.prevBtn = qs(root, '[data-lightbox-prev]');
    this.nextBtn = qs(root, '[data-lightbox-next]');
    this.closeBtns = qsa(root, '[data-lightbox-close]');
    this.skeleton = qs(root, '[data-lightbox-skeleton]');

    this.currentIndex = 0;
    this.isOpen = false;
    this.lastFocused = null;
    this.loadedIndexes = {};

    // zoom/pan state (per open image)
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this.panStart = null; // touch drag only — desktop pan follows the cursor directly

    // touch state
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchStartTime = 0;
    this.pinchStartDist = 0;
    this.pinchStartScale = 1;
    this.isPinching = false;
    this.lastTapTime = 0;

    this._bindHandlers();
    this._attachListeners();

    root.lightboxInstance = this;
  }

  ProductLightbox.prototype._bindHandlers = function () {
    // Bind once so add/removeEventListener reference the same function.
    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onPrevClick = this.prev.bind(this);
    this._onNextClick = this.next.bind(this);
    this._onCloseClick = this.close.bind(this);
    this._onThumbClick = this._onThumbClick.bind(this);
    this._onWheel = this._onWheel.bind(this);
    this._onDblClick = this._onDblClick.bind(this);
    this._onStageMouseMove = this._onStageMouseMove.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);
    this._onVariantChange = this._onVariantChange.bind(this);
  };

  ProductLightbox.prototype._attachListeners = function () {
    // Delegate trigger clicks — works even if triggers are re-rendered.
    document.addEventListener('click', this._onTriggerClick);
    

    this.closeBtns.forEach(function (btn) {
      btn.addEventListener('click', this._onCloseClick);
    }, this);

    if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrevClick);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNextClick);

    this.thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', this._onThumbClick);
    }, this);

    document.addEventListener('keydown', this._onKeydown);

    if (this.slidesWrap) {
      this.slidesWrap.addEventListener('wheel', this._onWheel, { passive: false });
      this.slidesWrap.addEventListener('dblclick', this._onDblClick);
      this.slidesWrap.addEventListener('mousemove', this._onStageMouseMove);
      this.slidesWrap.addEventListener('touchstart', this._onTouchStart, { passive: true });
      this.slidesWrap.addEventListener('touchmove', this._onTouchMove, { passive: false });
      this.slidesWrap.addEventListener('touchend', this._onTouchEnd);
    }

    // Variant sync, method 1 — theme dispatches this on selection change.
    document.addEventListener('product:variant-change', this._onVariantChange);

    // Variant sync, method 2 — zero-config fallback. This theme already
    // reflects the active variant's media as a data attribute on the
    // section wrapper (data-initial-variant-media-id). Watch it directly
    // so the gallery / triggers stay in sync even if nothing dispatches
    // the custom event above.
    this._observeVariantAttribute();
  };

  ProductLightbox.prototype._observeVariantAttribute = function () {
    var formHolder = this.root.closest('[data-form-holder]');
    if (!formHolder || typeof MutationObserver === 'undefined') return;

    var self = this;
    this._variantObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName !== 'data-initial-variant-media-id') return;
        var mediaId = formHolder.getAttribute('data-initial-variant-media-id');
        if (mediaId) self._syncToMediaId(mediaId);
      });
    });
    this._variantObserver.observe(formHolder, { attributes: true, attributeFilter: ['data-initial-variant-media-id'] });
  };

  ProductLightbox.prototype._syncToMediaId = function (mediaId) {
    var index = this.slides.findIndex(function (slide) {
      return slide.dataset.mediaId === String(mediaId);
    });
    if (index === -1) return;

    if (this.isOpen) {
      this.goTo(index);
    } else {
      // Update state quietly without opening the modal: active thumb/slide
      // classes inside the (closed) modal, plus the on-page trigger button.
      this.currentIndex = index;
      this.slides.forEach(function (slide, i) { slide.classList.toggle('is-active', i === index); });
      this.thumbs.forEach(function (thumb, i) { thumb.classList.toggle('is-active', i === index); });
    }

    var triggersRoot = document.querySelector('[data-lightbox-triggers][data-lightbox-for="' + this.root.dataset.lightboxId + '"]');
    if (triggersRoot) {
      qsa(triggersRoot, '[data-lightbox-trigger]').forEach(function (trigger) {
        trigger.classList.toggle('is-active', trigger.dataset.mediaId === String(mediaId));
      });
    }
  };

  ProductLightbox.prototype.destroy = function () {
    document.removeEventListener('click', this._onTriggerClick);
    document.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('product:variant-change', this._onVariantChange);

    this.closeBtns.forEach(function (btn) {
      btn.removeEventListener('click', this._onCloseClick);
    }, this);
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrevClick);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNextClick);
    this.thumbs.forEach(function (thumb) {
      thumb.removeEventListener('click', this._onThumbClick);
    }, this);

    if (this.slidesWrap) {
      this.slidesWrap.removeEventListener('wheel', this._onWheel);
      this.slidesWrap.removeEventListener('dblclick', this._onDblClick);
      this.slidesWrap.removeEventListener('mousemove', this._onStageMouseMove);
      this.slidesWrap.removeEventListener('touchstart', this._onTouchStart);
      this.slidesWrap.removeEventListener('touchmove', this._onTouchMove);
      this.slidesWrap.removeEventListener('touchend', this._onTouchEnd);
    }

    if (this._variantObserver) this._variantObserver.disconnect();

    this.root.lightboxInstance = null;
  };

  /* ---------------------------------------------------------------------
   * Open / close / navigate
   * ------------------------------------------------------------------- */
  ProductLightbox.prototype._onTriggerClick = function (e) {
    // Target native Pipeline gallery media elements or explicit triggers
    var target = e.target;
    
    // Ignore interactive control clicks (video player, 3D model viewer, buttons, links)
    if (target.closest('video, model-viewer, button, a, iframe, input, select')) return;

    var nativeMedia = target.closest(
      '[data-product-slideshow] [data-media-id], ' +
      '[data-product-slideshow] .product__media, ' +
      '[data-product-slideshow] .product-single__media, ' +
      '[data-product-slideshow] img, ' +
      '[data-lightbox-trigger]'
    );
    if (!nativeMedia) return;

    // Ensure we are in the product media container
    var slideshow = target.closest('[data-product-slideshow]');
    if (!slideshow && !nativeMedia.dataset.lightboxTrigger) return;

    e.preventDefault();

    var mediaId = nativeMedia.dataset.mediaId || nativeMedia.getAttribute('data-media-id');
    if (!mediaId) {
      var parentWithId = nativeMedia.closest('[data-media-id]');
      if (parentWithId) mediaId = parentWithId.dataset.mediaId || parentWithId.getAttribute('data-media-id');
    }

    var index = -1;
    if (mediaId) {
      index = this.slides.findIndex(function (slide) {
        return slide.dataset.mediaId === String(mediaId);
      });
    }

    if (index === -1 && slideshow) {
      var allMedia = qsa(slideshow, '[data-media-id], .product__media');
      var matchedEl = nativeMedia.closest('[data-media-id], .product__media') || nativeMedia;
      index = allMedia.indexOf(matchedEl);
    }

    if (index < 0) index = 0;
    this.open(index);
  };

  ProductLightbox.prototype.open = function (index) {
    if (this.isOpen) return;
    this.isOpen = true;
    this.lastFocused = document.activeElement;

    this.root.classList.add('is-open');
    document.body.classList.add('product-lightbox-lock');

    // Force reflow so the fade/scale transition runs.
    // eslint-disable-next-line no-unused-expressions
    this.root.offsetHeight;
    this.root.classList.add('is-visible');
    this.root.setAttribute('aria-hidden', 'false');

    this.goTo(index || 0, { instant: true });

    var self = this;
    window.setTimeout(function () {
      self.root.focus();
    }, 0);

    document.dispatchEvent(new CustomEvent('product-lightbox:open', {
      detail: { index: this.currentIndex, root: this.root }
    }));
  };

  ProductLightbox.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.root.classList.remove('is-visible');
    this.root.setAttribute('aria-hidden', 'true');
    this._resetZoom();

    var self = this;
    window.setTimeout(function () {
      self.root.classList.remove('is-open');
      document.body.classList.remove('product-lightbox-lock');
      if (self.lastFocused && typeof self.lastFocused.focus === 'function') {
        self.lastFocused.focus();
      }
    }, 250);

    document.dispatchEvent(new CustomEvent('product-lightbox:close', { detail: { root: this.root } }));
  };

  ProductLightbox.prototype.next = function () {
    this.goTo(this.currentIndex + 1);
  };

  ProductLightbox.prototype.prev = function () {
    this.goTo(this.currentIndex - 1);
  };

  ProductLightbox.prototype.goTo = function (index, opts) {
    opts = opts || {};
    var count = this.slides.length;
    if (!count) return;
    index = clamp(index, 0, count - 1);

    this._resetZoom();

    this.slides.forEach(function (slide, i) {
      slide.classList.toggle('is-active', i === index);
    });

    this.thumbs.forEach(function (thumb, i) {
      thumb.classList.toggle('is-active', i === index);
      thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
    });

    this.currentIndex = index;
    this._updateCounter();
    this._updateNavState();
    this._scrollThumbIntoView(index);
    this._loadSlide(index);
    this._loadSlide(index - 1); // preload previous
    this._loadSlide(index + 1); // preload next
  };

  ProductLightbox.prototype.goToMediaId = function (mediaId) {
    var index = this.slides.findIndex(function (slide) {
      return slide.dataset.mediaId === String(mediaId);
    });
    if (index > -1) this.goTo(index);
    return index;
  };

  ProductLightbox.prototype._updateCounter = function () {
    if (!this.counter) return;
    this.counter.textContent = (this.currentIndex + 1) + ' / ' + this.slides.length;
  };

  ProductLightbox.prototype._updateNavState = function () {
    // Looping nav — keep enabled at edges for a premium, uninterrupted feel.
    if (this.prevBtn) this.prevBtn.disabled = this.slides.length < 2;
    if (this.nextBtn) this.nextBtn.disabled = this.slides.length < 2;
  };

  ProductLightbox.prototype._scrollThumbIntoView = function (index) {
    var thumb = this.thumbs[index];
    if (!thumb || !this.thumbsRail) return;
    var railRect = this.thumbsRail.getBoundingClientRect();
    var thumbRect = thumb.getBoundingClientRect();
    var isRow = this.thumbsRail.scrollWidth > this.thumbsRail.clientWidth &&
      this.thumbsRail.scrollHeight <= this.thumbsRail.clientHeight;

    if (isRow) {
      if (thumbRect.left < railRect.left || thumbRect.right > railRect.right) {
        thumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    } else if (thumbRect.top < railRect.top || thumbRect.bottom > railRect.bottom) {
      thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  ProductLightbox.prototype._onThumbClick = function (e) {
    var thumb = e.currentTarget;
    this.goTo(parseInt(thumb.dataset.index, 10));
  };

  /* ---------------------------------------------------------------------
   * Lazy / progressive image loading + preloading
   * ------------------------------------------------------------------- */
  ProductLightbox.prototype._loadSlide = function (index) {
    if (index < 0 || index >= this.slides.length || this.loadedIndexes[index]) return;
    var slide = this.slides[index];
    var img = qs(slide, '[data-lightbox-image]');
    if (!img) {
      this.loadedIndexes[index] = true;
      return;
    }

    var showSkeleton = index === this.currentIndex;
    if (showSkeleton && this.skeleton) this.skeleton.classList.add('is-visible');

    var fullSrc = img.dataset.src;
    var srcset = img.dataset.srcset;
    var sizes = img.dataset.sizes;

    var finish = function () {
      img.classList.add('is-loaded');
      if (showSkeleton && this.skeleton) this.skeleton.classList.remove('is-visible');
    }.bind(this);

    if (img.complete && img.currentSrc && img.dataset.loaded === 'true') {
      finish();
      this.loadedIndexes[index] = true;
      return;
    }

    var loader = new Image();
    loader.onload = function () {
      if (sizes) img.sizes = sizes;
      if (srcset) img.srcset = srcset;
      img.src = fullSrc;
      img.dataset.loaded = 'true';
      finish();
    };
    loader.onerror = finish;
    if (sizes) loader.sizes = sizes;
    if (srcset) loader.srcset = srcset;
    loader.src = fullSrc;

    this.loadedIndexes[index] = true;
  };

  /* ---------------------------------------------------------------------
   * Keyboard + focus trap
   * ------------------------------------------------------------------- */
  ProductLightbox.prototype._onKeydown = function (e) {
    if (!this.isOpen) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
      return;
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.prev();
      return;
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.next();
      return;
    }
    if (e.key === 'Tab') {
      this._trapFocus(e);
    }
  };

  ProductLightbox.prototype._trapFocus = function (e) {
    var focusable = qsa(this.root, 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      .filter(function (el) { return el.offsetParent !== null; });
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  /* ---------------------------------------------------------------------
   * Zoom: wheel, double-click, cursor-follow panning (desktop)
   * ------------------------------------------------------------------- */
  ProductLightbox.prototype._activeImage = function () {
    var slide = this.slides[this.currentIndex];
    return slide ? qs(slide, '[data-lightbox-image]') : null;
  };

  ProductLightbox.prototype._applyTransform = function (img) {
    if (!img) return;
    img.style.setProperty('--scale', this.scale);
    img.style.setProperty('--tx', this.tx + 'px');
    img.style.setProperty('--ty', this.ty + 'px');
    img.classList.toggle('is-zoomed', this.scale > 1);
  };

  ProductLightbox.prototype._constrainPan = function (img) {
    if (!img) return;
    var rect = img.getBoundingClientRect();
    var stageRect = this.slidesWrap.getBoundingClientRect();
    var overflowX = Math.max(0, (rect.width - stageRect.width) / 2);
    var overflowY = Math.max(0, (rect.height - stageRect.height) / 2);
    this.tx = clamp(this.tx, -overflowX, overflowX);
    this.ty = clamp(this.ty, -overflowY, overflowY);
  };

  ProductLightbox.prototype._resetZoom = function () {
    var img = this._activeImage();
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    if (img) {
      img.classList.add('is-transitioning');
      this._applyTransform(img);
      window.setTimeout(function () { img.classList.remove('is-transitioning'); }, 200);
    }
  };

  ProductLightbox.prototype._zoomAt = function (targetScale, clientX, clientY) {
    var img = this._activeImage();
    if (!img) return;
    var rect = img.getBoundingClientRect();
    var prevScale = this.scale;
    targetScale = clamp(targetScale, ZOOM_MIN, ZOOM_MAX);

    // Anchor the zoom around the cursor/tap position.
    var offsetX = clientX - (rect.left + rect.width / 2);
    var offsetY = clientY - (rect.top + rect.height / 2);
    var scaleRatio = targetScale / prevScale;

    this.tx = this.tx - offsetX * (scaleRatio - 1);
    this.ty = this.ty - offsetY * (scaleRatio - 1);
    this.scale = targetScale;

    if (this.scale <= ZOOM_MIN) {
      this.scale = ZOOM_MIN;
      this.tx = 0;
      this.ty = 0;
    } else {
      this._constrainPan(img);
    }

    img.classList.add('is-transitioning');
    this._applyTransform(img);
    window.setTimeout(function () { img.classList.remove('is-transitioning'); }, 200);
  };

  ProductLightbox.prototype._onWheel = function (e) {
    if (!this.isOpen) return;
    var img = this._activeImage();
    if (!img) return;
    e.preventDefault();

    var delta = -e.deltaY * ZOOM_STEP_WHEEL * this.scale * 100;
    var target = clamp(this.scale + delta, ZOOM_MIN, ZOOM_MAX);
    this._zoomAt(target, e.clientX, e.clientY);
  };

  ProductLightbox.prototype._onDblClick = function (e) {
    var img = this._activeImage();
    if (!img) return;
    if (this.scale > ZOOM_MIN) {
      this._resetZoom();
    } else {
      this._zoomAt(DOUBLE_TAP_ZOOM, e.clientX, e.clientY);
    }
  };

  /**
   * Cursor-follow panning: once zoomed, the visible portion of the image
   * tracks the mouse position directly (no click-and-drag required) — the
   * cursor's position within the stage maps to the equivalent position
   * within the zoomed image, like a magnifier.
   */
  ProductLightbox.prototype._onStageMouseMove = function (e) {
    if (this.scale <= ZOOM_MIN) return;
    var img = this._activeImage();
    if (!img) return;

    var stageRect = this.slidesWrap.getBoundingClientRect();
    var percentX = clamp((e.clientX - stageRect.left) / stageRect.width, 0, 1);
    var percentY = clamp((e.clientY - stageRect.top) / stageRect.height, 0, 1);

    var rect = img.getBoundingClientRect();
    var overflowX = Math.max(0, (rect.width - stageRect.width) / 2);
    var overflowY = Math.max(0, (rect.height - stageRect.height) / 2);

    this.tx = -(percentX - 0.5) * overflowX * 2;
    this.ty = -(percentY - 0.5) * overflowY * 2;
    this._applyTransform(img);
  };

  /* ---------------------------------------------------------------------
   * Touch: swipe between slides, pinch to zoom, double-tap to zoom
   * ------------------------------------------------------------------- */
  ProductLightbox.prototype._onTouchStart = function (e) {
    if (e.touches.length === 2) {
      this.isPinching = true;
      this.pinchStartDist = distanceBetweenTouches(e.touches);
      this.pinchStartScale = this.scale;
      return;
    }

    this.isPinching = false;
    var t = e.touches[0];
    this.touchStartX = t.clientX;
    this.touchStartY = t.clientY;
    this.touchStartTime = Date.now();

    if (this.scale > ZOOM_MIN) {
      this.panStart = { x: t.clientX, y: t.clientY, tx: this.tx, ty: this.ty, time: Date.now() };
    }
  };

  ProductLightbox.prototype._onTouchMove = function (e) {
    var img = this._activeImage();

    if (e.touches.length === 2 && this.isPinching) {
      e.preventDefault();
      var dist = distanceBetweenTouches(e.touches);
      var ratio = dist / (this.pinchStartDist || dist);
      var target = clamp(this.pinchStartScale * ratio, ZOOM_MIN, ZOOM_MAX);
      var mid = midpoint(e.touches);
      this._zoomAt(target, mid.x, mid.y);
      return;
    }

    if (this.scale > ZOOM_MIN && this.panStart) {
      e.preventDefault();
      var t = e.touches[0];
      this.tx = this.panStart.tx + (t.clientX - this.panStart.x);
      this.ty = this.panStart.ty + (t.clientY - this.panStart.y);
      this._constrainPan(img);
      this._applyTransform(img);
    }
    // When not zoomed, allow the browser to handle native scroll/swipe
    // tracking; the swipe decision happens on touchend.
  };

  ProductLightbox.prototype._onTouchEnd = function (e) {
    if (this.isPinching) {
      this.isPinching = false;
      if (this.scale <= ZOOM_MIN) this._resetZoom();
      return;
    }

    if (this.scale > ZOOM_MIN) {
      this.panStart = null;
      return; // panning while zoomed does not trigger slide navigation
    }

    var touch = e.changedTouches[0];
    var dx = touch.clientX - this.touchStartX;
    var dy = touch.clientY - this.touchStartY;
    var dt = Math.max(1, Date.now() - this.touchStartTime);
    var velocity = Math.abs(dx) / dt;

    // Double-tap to zoom
    var now = Date.now();
    var isTap = Math.abs(dx) < 10 && Math.abs(dy) < 10;
    if (isTap) {
      if (now - this.lastTapTime < 300) {
        this._zoomAt(DOUBLE_TAP_ZOOM, touch.clientX, touch.clientY);
        this.lastTapTime = 0;
      } else {
        this.lastTapTime = now;
      }
      return;
    }

    if (Math.abs(dx) > Math.abs(dy) && (Math.abs(dx) > SWIPE_THRESHOLD || velocity > SWIPE_VELOCITY_THRESHOLD)) {
      if (dx < 0) this.next(); else this.prev();
    }
  };

  /* ---------------------------------------------------------------------
   * Variant sync
   * Theme dispatches: document.dispatchEvent(new CustomEvent(
   *   'product:variant-change', { detail: { mediaId: variant.featured_media.id }}
   * ));
   * ------------------------------------------------------------------- */
  ProductLightbox.prototype._onVariantChange = function (e) {
    if (!e.detail || !e.detail.mediaId) return;
    var index = this.goToMediaId(e.detail.mediaId);
    if (index === -1 && !this.isOpen) return;
    if (!this.isOpen) {
      // Keep the closed-state trigger thumbnails / active state in sync too.
      this._scrollThumbIntoView(this.currentIndex);
    }
  };

  /* ---------------------------------------------------------------------
   * Auto-init: scan the document, and re-init on Shopify section events.
   * ------------------------------------------------------------------- */
  var registry = new WeakMap();

  function initAll(scope) {
    scope = scope || document;

    // Deduplicate globally in the document first to prevent duplicate lightboxes.
    var allElements = qsa(document, '[data-lightbox]');
    if (allElements.length > 1) {
      for (var i = 1; i < allElements.length; i++) {
        var instance = registry.get(allElements[i]);
        if (instance) {
          instance.destroy();
          registry.delete(allElements[i]);
        }
        if (allElements[i].parentNode) {
          allElements[i].parentNode.removeChild(allElements[i]);
        }
      }
    }

    qsa(scope, '[data-lightbox]').forEach(function (root) {
      if (registry.has(root)) return;
      var instance = new ProductLightbox(root);
      registry.set(root, instance);
    });
  }

  function destroyAll(scope) {
    scope = scope || document;
    qsa(scope, '[data-lightbox]').forEach(function (root) {
      var instance = registry.get(root);
      if (instance) {
        instance.destroy();
        registry.delete(root);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  // Shopify theme editor: re-init/teardown when this section is reloaded.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    destroyAll(e.target);
  });

  window.ProductLightbox = {
    init: function (root) {
      if (registry.has(root)) return registry.get(root);
      var instance = new ProductLightbox(root);
      registry.set(root, instance);
      return instance;
    },
    destroy: function (root) {
      var instance = registry.get(root);
      if (instance) {
        instance.destroy();
        registry.delete(root);
      }
    },
    get: function (root) {
      return registry.get(root);
    }
  };
})(window, document);


